import { ChangeEvent, FC } from 'react';
import { Input, List } from 'antd';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { BeneficiaryGroup } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { useAuthStore } from '@/shared/store/auth.store';
import { useProfileStore } from '@/shared/store/profile.store';

import { CategoryItem } from './CategoryItem';
import { NotItems } from './NotItems';
import { TabDescription } from './TabDescription';
import { DescriptionInfo, NextOwnerUser, SetInviteUser } from './type';

const groupNameLabel: Record<BeneficiaryGroup, string> = {
  [BeneficiaryGroup.CIRCLE]: 'Circle',
  [BeneficiaryGroup.FAMILY_CLAN]: 'Family Clan',
  [BeneficiaryGroup.FAMILY]: 'Family',
};

const groupOrder: Array<BeneficiaryGroup> = [
  BeneficiaryGroup.FAMILY,
  BeneficiaryGroup.FAMILY_CLAN,
  BeneficiaryGroup.CIRCLE,
];

type CategoryProps = {
  isShowStatus?: boolean;
  dataSource: Array<NextOwnerUser>;
  descriptionInfo?: DescriptionInfo;
  invitedUsers: Array<string>;
  searchValue: string;
  isSelectPersonMode?: boolean;
  handleSelect: (value?: string) => void;
  searchInputHandler: (event: ChangeEvent<HTMLInputElement>) => void;
  setModalInviteUserState: SetInviteUser;
  mode: 'multiple' | 'tags' | undefined;
  getIsChecked: (value: string | undefined) => boolean | undefined;
};

export const Category: FC<CategoryProps> = ({
  isShowStatus = false,
  dataSource,
  descriptionInfo,
  invitedUsers,
  searchValue,
  isSelectPersonMode,
  handleSelect,
  searchInputHandler,
  setModalInviteUserState,
  getIsChecked,
  mode,
}) => {
  const mainUseEmail = useAuthStore((s) => s.account?.email);
  const [firstName, lastName] = useProfileStore((s) => [s.firstName, s.lastName]);

  const groupedUsers = dataSource.reduce(
    (groups: Record<BeneficiaryGroup, Array<(typeof dataSource)[number]>>, user) => {
      const group = user.group;

      if (!group) {
        return groups;
      }

      if (!groups[group]) {
        groups[group] = [];
      }

      groups[group].push(user);
      return groups;
    },

    {
      [BeneficiaryGroup.CIRCLE]: [],
      [BeneficiaryGroup.FAMILY_CLAN]: [],
      [BeneficiaryGroup.FAMILY]: [],
    },
  );

  const groupList = groupOrder.filter((item) => groupedUsers[item].length > 0);
  const mainUserDat = {
    value: 'me',
    label: getPersonFullName({ firstName, lastName }),
    email: mainUseEmail ?? '-',
  };

  return (
    <Content>
      {descriptionInfo && (
        <TabDescription title={descriptionInfo.title} text={descriptionInfo.text} />
      )}
      <Search placeholder="Search" onChange={searchInputHandler} value={searchValue} />
      <div>
        {dataSource.length === 0 && !isSelectPersonMode ? (
          <NotItems />
        ) : (
          <>
            {isSelectPersonMode && (
              <Wrapper key={'me'}>
                <h3>Me</h3>
                <List
                  style={{ background: '#ffffff' }}
                  itemLayout="horizontal"
                  dataSource={[mainUserDat]}
                  renderItem={(user) => (
                    <CategoryItem
                      handleSelect={handleSelect}
                      isShowStatus={isShowStatus}
                      newUserEmail={localStorage.getItem(`new-family-member-added`)}
                      user={user}
                      invitedUsers={invitedUsers}
                      setModalInviteUserState={setModalInviteUserState}
                      searchValue={searchValue}
                      mode={mode}
                      checked={getIsChecked(user.value)}
                    />
                  )}
                />
              </Wrapper>
            )}
            {groupList.map((group) => (
              <Wrapper key={group}>
                <h3>{groupNameLabel[group]}</h3>
                <List
                  style={{ background: '#ffffff' }}
                  itemLayout="horizontal"
                  dataSource={groupedUsers[group]}
                  renderItem={(user) => (
                    <CategoryItem
                      user={user}
                      isShowStatus={isShowStatus}
                      newUserEmail={localStorage.getItem(`new-family-member-added`)}
                      invitedUsers={invitedUsers}
                      searchValue={searchValue}
                      handleSelect={handleSelect}
                      setModalInviteUserState={setModalInviteUserState}
                      mode={mode}
                      checked={getIsChecked(user.value)}
                    />
                  )}
                />
              </Wrapper>
            ))}
          </>
        )}
      </div>
    </Content>
  );
};

const Wrapper = styled.div`
  background: ${COLORS.colorPrimaryBg};
  margin-top: 12px;
`;

const Content = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr;
  position: relative;
  overflow-y: auto;
  height: 60vh;
`;

const Search = styled(Input)`
  &&& {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;
